import React from 'react';
import { withTranslation } from 'react-i18next';
import {
    Badge,
    Button,
    Col,
    FormGroup,
    Row,
    ListGroup,
    ListGroupItem,
    Alert,
    Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import * as moment from 'moment';
import {
    faExclamationTriangle,
    faPlus,
    faSave,
    faUserClock,
    faTrashAlt,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18n from '@s20/i18n';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { formatTime24Hr, getDateTimeFromUtc } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, {
    ApiRoutes,
    AppNavPaths,
    MomentDateFormat,
    MomentTimeFormat,
    ServiceResponseResult,
    TimesheetReviewType,
} from '../Common';
import {
    AppPageForm,
    FlexBetweenRow,
    FlexCenterRow,
    FlexColumnCenter,
    FlexColumnStart,
    FlexEndRow,
    FlexStartRow,
    FormBlocker,
    FormLabel,
    Input,
    onFieldChange,
    onReactSelectChanged,
    PageWrap,
    ToastMessage,
    toasty,
    Textarea,
} from '../common/forms/FormElements';
import { handleFormSaveError } from '../common/forms/ValidationError';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import authService from '../api-authorization/AuthorizeService';
import { NotAuthorizedPage } from '../status/StatusCodes';
import { NumericInput } from '../common/forms/NumericInput';
// import cls from 'classnames';
import './SelfDispatchTimesheetForm.scss';
import SignatureButton from '../common/forms/SignatureButton';
import SignatureImage from '../common/forms/SignatureImage';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { ChargeTypeUnits } from '../chargeType/ChargeType';
import SurveyQuestion, {
    onSelectAnswerChanged,
    onSelectChanged,
    onTextAnswerChanged,
} from '../formsAndSurveys/SurveyQuestion';
import TimePicker from '../common/forms/TimePicker';
import { ModalForm } from '../common/forms/ModalForm';
import { ConfirmDelete } from '../common/Modal';
import { Answers } from './Answers';
import { TimesheetStatus } from './Timesheet';
import { ClientTimesheetSignature } from './ClientTimesheetSignature';
import { TimesheetSignatureType } from './ClientReview';
import {
    SelfDispatchTimesheet,
    SelfDispatchTimesheetDetailViewModel,
    SelfDispatchTimesheetReviewViewModel,
} from './SelfDispatching';

/**
 * getDuration
 *
 * Helper function to either print a duration between two moment times in hours/minutes,
 * or return the total amount of minutes between the two times. (Negative returns flat zero).
 *
 * R. Li Casanova
 *
 * @param {string} start HH:mm time format
 * @param {string} end HH:mm time format
 * @param {string} breakStart HH:mm time format
 * @param {string} breakEnd HH:mm time format
 * @param {boolean} bPrint print as string, or return total minutes
 */
const getDuration = (date, start, end, breakStart, breakEnd, bPrint) => {
    // Print duration between start and end time
    const totalDuration = '0h 00m';
    const dateFormat = 'YYYY-MM-DD HH:mm';

    if (start && end) {
        const mDate = moment(date).format('YYYY-MM-DD');
        const strStart = `${mDate} ${start}`;
        const strEnd = `${mDate} ${end}`;

        const mStartTime = moment(strStart, dateFormat);
        const mEndTime = moment(strEnd, dateFormat);

        // Account for times stretching into the next morning/day.
        if (mEndTime.isBefore(mStartTime)) {
            mEndTime.add(1, 'day');
        }

        const duration = moment.duration(mEndTime.diff(mStartTime));

        let breakDurationString = '';

        if (breakStart && breakEnd) {
            const breakStartString = `${mDate} ${breakStart}`;
            const breakEndString = `${mDate} ${breakEnd}`;

            const breakStartTime = moment(breakStartString, dateFormat);
            const breakEndTime = moment(breakEndString, dateFormat);

            if (breakEndTime.isBefore(breakStartTime)) {
                breakEndTime.add(1, 'day');
            }

            const breakDuration = moment.duration(
                breakEndTime.diff(breakStartTime),
            );

            duration.subtract(breakDuration);

            const breakTotalMinutes = parseInt(breakDuration.asMinutes());

            const breakHours = Math.floor(breakTotalMinutes / 60);
            const breakMinutes = (breakTotalMinutes % 60)
                .toString()
                .padStart(2, '0');

            breakDurationString = breakHours > 0
                ? ` (${breakHours}h ${breakMinutes}m break)`
                : ` (${breakMinutes}m break)`;
        }

        const totalMinutes = parseInt(duration.asMinutes());

        if (totalMinutes >= 0) {
            const hours = Math.floor(totalMinutes / 60);
            const mins = (totalMinutes % 60).toString().padStart(2, '0');

            if (bPrint) return `${hours}h ${mins}m${breakDurationString}`;
            return totalMinutes;
        }
    }
    return bPrint ? totalDuration : 0.0;
};

const compareFlatDetail = (a, b) => {
    if (a.chargeTypeName > b.chargeTypeName) {
        return -1;
    }
    if (a.chargeTypeName < b.chargeTypeName) {
        return 1;
    }
    return 0;
};

const compareTimeDetailVm = (a, b) => {
    const mStartA = formatTime24Hr(a.start);
    const mStartB = formatTime24Hr(b.start);

    if (mStartA.isBefore(mStartB)) {
        return -1;
    }
    if (mStartA.isAfter(mStartB)) {
        return 1;
    }
    return 0;
};

// Sorts entries by hours units chrono, then flat charges by charge name.
const sortTimesheetEntries = (timesheetEntries) => {
    if ((timesheetEntries ?? []).length > 1) {
        const hoursBasedEntries = timesheetEntries.filter(
            (x) => x.chargeTypeUnitsId === ChargeTypeUnits.Hours,
        ) ?? [];
        const flatEntries = timesheetEntries.filter(
            (x) => x.chargeTypeUnitsId === ChargeTypeUnits.Flat,
        ) ?? [];
        hoursBasedEntries.sort(compareTimeDetailVm);
        flatEntries.sort(compareFlatDetail);
        const sorted = [...hoursBasedEntries, ...flatEntries];
        return sorted;
    }
    return timesheetEntries;
};

/// Takes an entry and compares against the other timesheet entries to see if there's overlap in a range.
const timeEntryOverlaps = (entry, timeEntries) => {
    let overlaps = false;

    if (!timeEntries.length) return overlaps;

    timeEntries.sort(compareTimeDetailVm);

    const entryStart = formatTime24Hr(entry.start);
    const entryEnd = formatTime24Hr(entry.end);
    for (const e of timeEntries) {
        const start = formatTime24Hr(e.start);
        const end = formatTime24Hr(e.end);
        if (
            // Test the new entry against the iterated item
            (entryStart.isAfter(start) && entryStart.isBefore(end))
            || (entryEnd.isAfter(start) && entryEnd.isBefore(end))
            // Test the iterated item against the new entry.
            // Must be done in case the new entry is outside the bounds of the iterated item
            // e.g. 12AM - 6PM against 6:30AM - 12PM
            || (start.isAfter(entryStart) && start.isBefore(entryEnd))
            || (end.isAfter(entryStart) && end.isBefore(entryEnd))
        ) {
            overlaps = true;
            break;
        }
    }
    return overlaps;
};

const isBreakAllowed = (selectedContractChargeType) => selectedContractChargeType
    && selectedContractChargeType.configurations
    && selectedContractChargeType.configurations.length
    && selectedContractChargeType.configurations.some((c) => c.canBreak);

const getBreakName = (selectedContractChargeType) => (isBreakAllowed(selectedContractChargeType)
    ? selectedContractChargeType.configurations.find((c) => c.canBreak)
        .breakDescription
    : null);

const ContractNotSetUpPage = ({
    nonFlagging,
}) => (
    <PageWrap>
        <FlexColumnCenter>
            <span className="display-1 d-block text-danger text-center">
                {nonFlagging
                    ? i18n.t('timesheet:nonFlagContractNotCreated')
                    : i18n.t('timesheet:selfDispatchContractNotCreated')}
            </span>
            <span className="h4 text-danger text-center">
                {i18n.t('timesheet:nonFlagSetupNotComplete')}
            </span>
            <a
                href="/"
                className="btn btn-link"
            >
                {i18n.t('common:backToHome')}
            </a>
        </FlexColumnCenter>
    </PageWrap>
);

class SelfDispatchTimesheetForm extends React.Component {
    // #region FORM SETUP
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.addTimeFormRef = React.createRef();

        this.clientSignatureRef = React.createRef();
        this.state = {
            contracts: [],
            equipmentOptions: [],
            timesheet: null,
            loading: true,
            saving: false,
            showAddChargeModal: false,
            currentTimeEntry: null,
            addTimeFormValidated: false,
            editable: false,
            clientSigIsComplete: false,
            equipmentLoading: false,
            errors: [],
        };
        this.handlers.change = this.handlers.change.bind(this);
        this.handlers.select = this.handlers.select.bind(this);
        this.onDetailTextAnswerChanged = this.onDetailTextAnswerChanged.bind(this);
        this.onDetailSelectAnswerChanged = this.onDetailSelectAnswerChanged.bind(this);
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    componentWillUnmount() {
        authService.unsubscribe(this._subscription);
    }

    onAddTimeClicked = () => this.setState({
        editingTimeIndex: null,
        showAddChargeModal: true,
        selectedDetail: new SelfDispatchTimesheetDetailViewModel(),
        addTimeFormValidated: false,
    });

    onAddTimeSubmit = () => {
        const {
            timesheet, selectedDetail, editingTimeIndex, editable,
        } = {
            ...this.state,
        };

        if (!editable) return null;

        const creatingNew = Boolean(Number.isNaN(parseInt(editingTimeIndex)));

        if (selectedDetail.chargeTypeUnitsName === 'Hours') {
            // Validate the entry itself.  A valid range will have a duration > 0.
            const rangeDuration = getDuration(
                timesheet.date,
                selectedDetail.start,
                selectedDetail.end,
                selectedDetail.breakStart,
                selectedDetail.breakEnd,
                false,
            );
            if (rangeDuration <= 0) {
                toasty.error(this.props.t('timesheet:invalidTimeRange'));
                return false;
            }

            // If we are not editing, attempt to push the entry.
            if (creatingNew) {
                // RLC: Check if new entry will overlap.
                if (
                    !timeEntryOverlaps({ ...selectedDetail }, [
                        ...timesheet.timeEntries,
                    ])
                ) {
                    selectedDetail.value = rangeDuration;
                    timesheet.timeEntries.push({ ...selectedDetail });
                } else {
                    toasty.error(this.props.t('timesheet:timeEntryOverlap'));
                    return false;
                }

                // Otherwise, check to see if the edit will cause a time overlap.
            } else {
                // RLC: Spread and splice, to avoid mutating state while we check the newly modified entry.
                //     Remove the edited entry from the array first, only compare against the other entries.
                const toCheck = [...timesheet.timeEntries];
                toCheck.splice(editingTimeIndex, 1);

                // Don't check flat charges.
                const entriesToCheck = (toCheck ?? []).filter(
                    (x) => x.chargeTypeUnitsId === ChargeTypeUnits.Hours,
                );

                if (
                    !timeEntryOverlaps({ ...selectedDetail }, [
                        ...entriesToCheck,
                    ])
                ) {
                    // We are all good, overwrite the new state.
                    timesheet.timeEntries[editingTimeIndex] = {
                        ...selectedDetail,
                    };
                } else {
                    toasty.error(this.props.t('timesheet:timeEntryOverlap'));
                    return false;
                }
            }
        } else if (creatingNew) timesheet.timeEntries.push({ ...selectedDetail });
        else timesheet.timeEntries[editingTimeIndex] = { ...selectedDetail };

        // Order the user's time entries.  Order flat charges after the regular time detail.
        timesheet.timeEntries = sortTimesheetEntries([
            ...timesheet.timeEntries,
        ]);

        this.setState({
            timesheet,
            selectedDetail: null,
            showAddChargeModal: false,
        });
    };

    onChargeChange = (ev) => {
        const { selectedDetail } = { ...this.state };
        const val = ev.target.value;
        const { name } = ev.target;

        // We display the options in 12-hour format, but the db has 24.
        const timeValue = ev.target.value
            ? moment(val, [MomentTimeFormat.AnteTwelveHour]).format(
                MomentTimeFormat.TwentyFourHourWithLeading,
            )
            : '';

        selectedDetail[name] = name === 'value' ? val : timeValue;

        if (['breakStart', 'breakEnd'].some((v) => v == name) && !timeValue) {
            selectedDetail.breakStart = '';
            selectedDetail.breakEnd = '';
        }

        this.setState({
            selectedDetail,
            addTimeFormValidated: false /* reset validation to avoid highlights when changing info */,
        });
    };

    // #region TIME ENTRY
    onChargeTypeChanged = async (selection) => {
        const { selectedDetail } = { ...this.state };

        if (!selection) {
            selectedDetail.start = null;
            selectedDetail.end = null;
            selectedDetail.chargeTypeId = null;
            selectedDetail.chargeTypeUnitsId = null;
            selectedDetail.chargeTypeUnitsName = null;
        } else {
            selectedDetail.chargeTypeId = selection.chargeTypeId;
            selectedDetail.chargeTypeName = selection.description;
            const chargeTypeUnitsId = selection.chargeUnits === 'Hours'
                ? ChargeTypeUnits.Hours
                : ChargeTypeUnits.Flat;
            selectedDetail.chargeTypeUnitsId = chargeTypeUnitsId;
            selectedDetail.chargeTypeUnitsName = selection.chargeUnits;
        }

        if (selection.equipment) {
            this.setState({ equipmentLoading: true });
            const equipmentOptions = await httpClient.js(
                ApiRoutes.selfDispatchTimesheet.equipmentByChargeType(
                    selection.chargeTypeId,
                ),
            );
            this.setState({ equipmentOptions, equipmentLoading: false });
        }
        this.setState({ selectedDetail });
    };

    onClearErrors = () => this.setState({ errors: [] });

    onClientSigSavedCallback = (sigData) => {
        const { timesheet } = { ...this.state };
        if (!timesheet.clientReview) {
            timesheet.clientReview = new SelfDispatchTimesheetReviewViewModel();
            timesheet.clientReview.reviewTypeId = TimesheetReviewType.Client;
        }
        timesheet.clientReview.signatureData = sigData;
        this.setState({ timesheet });
    };
    // #endregion

    onDetailSelectAnswerChanged = onSelectAnswerChanged;

    onDetailSelectChanged = onSelectChanged;

    // #region QUESTIONS/RESPONSES
    onDetailTextAnswerChanged = onTextAnswerChanged;

    onEditTimeClicked = (detailVM, inx) => {
        const { contracts, timesheet } = { ...this.state };

        if (detailVM.removed || detailVM.isAdjustment) return null;

        const chargeType = contracts
            .find((x) => x.id === timesheet.contractId)
            .contractChargeTypes.find(
                (x) => x.chargeTypeId === detailVM.chargeTypeId,
            );

        this.setState({
            editingTimeIndex: inx,
            selectedDetail: { ...detailVM },
            showAddChargeModal: true,
        }, async () => {
            await this.onChargeTypeChanged({ ...chargeType });
        });
    };

    onEmployeeSigSavedCallback = (sigData) => {
        const { timesheet } = { ...this.state };
        if (!timesheet.employeeReview) {
            timesheet.employeeReview = new SelfDispatchTimesheetReviewViewModel();
            timesheet.employeeReview.reviewTypeId = TimesheetReviewType.Employee;
            timesheet.employeeReview.signatureTypeId = TimesheetSignatureType.Employee;
        }
        timesheet.employeeReview.signatureData = sigData;
        this.setState({ timesheet });
    };

    onEquipmentChanged = (selection) => {
        const { selectedDetail } = { ...this.state };

        if (!selection) {
            selectedDetail.equipmentId = null;
            selectedDetail.equipmentName = null;
            selectedDetail.equipmentTypeId = null;
            selectedDetail.equipmentType = null;
        } else {
            selectedDetail.equipmentId = selection.id;
            selectedDetail.equipmentName = selection.description;
            selectedDetail.equipmentTypeId = selection.equipmentType.id;
            selectedDetail.equipmentType = selection.equipmentType.description;
        }

        this.setState({ selectedDetail });
    };

    onRemoveTimeEntryClicked = (inx) => {
        const { timesheet } = { ...this.state };
        if (!Number.isNaN(parseInt(inx))) {
            timesheet.timeEntries.splice(inx, 1);
            this.setState({ timesheet, showAddChargeModal: false });
        }
    };

    onSelectedContractChanged = (selection) => {
        this.setState(
            (state) => (
                (state.timesheet.contractId = selection?.value ?? null), state
            ),
        );
    };

    onSelectedDetailNotesChanged = (ev) => {
        const { value } = ev.target;
        this.setState((state) => ((state.selectedDetail.notes = value), state));
    };

    onSubmit = async () => {
        const { timesheet } = { ...this.state };
        this.onClearErrors();
        this.setSaving(true);

        if ([
            AppNavPaths.SelfDispatchTimesheetNew,
            AppNavPaths.NonFlaggingTimesheetNew,
        ].includes(this.props.location.pathname)) {
            await this.create(timesheet);
        } else {
            await this.update(timesheet);
        }
    };

    getTimesheetStatusAlert = () => {
        const { t } = this.props;
        const { timesheet } = { ...this.state };
        const { statusId, statusHistory } = { ...timesheet };

        let statusText = '';
        let colorContext = '';

        const history = statusHistory.find(
            (h) => h.timesheetStatusId === statusId,
        );
        const historyDate = getDateTimeFromUtc(history.changedOn);

        switch (statusId) {
            case TimesheetStatus.Submitted:
                statusText = (
                    <FlexColumnStart className="w-100">
                        <span className="ml-2">{t('timesheet:submittedOn', { when: historyDate })}</span>

                        <small>
                            {t('timesheet:byUser', { user: history.changedBy })}
                        </small>
                    </FlexColumnStart>
                );
                colorContext = 'secondary';
                break;

            case TimesheetStatus.Approved:
                statusText = (
                    <FlexColumnStart className="w-100">
                        <span className="ml-2">{t('timesheet:approvedOn', { when: historyDate })}</span>

                        <small>
                            {t('timesheet:byUser', { user: history.changedBy })}
                        </small>
                    </FlexColumnStart>
                );
                colorContext = 'success';
                break;

            case TimesheetStatus.Rejected: {
                const [rejection] = timesheet.rejections;

                statusText = (
                    <>
                        <small className="w-100 text-left border-bottom border-danger">
                            {t('timesheet:rejectedBy')}
                            {' '}
                            {t('timesheet:rejectionReasonOn', { reason: rejection.item1, when: historyDate })}
                        </small>
                        <span className="w-100 pt-1 text-left">
                            <b>{rejection.item2}</b>
                        </span>
                    </>
                );
                colorContext = 'danger';
                break;
            }
            default:
                return null;
        }

        return (
            <Alert
                className="m-0 pt-1 pb-2 pl-3 pr-3 text-center d-flex flex-row align-items-start"
                color={colorContext}
            >
                <FlexColumnStart className="flex-fill">
                    {statusText}
                </FlexColumnStart>
            </Alert>
        );
    };
    // #endregion

    getTotalDuration = () => {
        const { timesheet } = { ...this.state };
        const { t } = this.props;
        const totalMinutes = (timesheet?.timeEntries ?? [])
            .map((x) => (x.chargeTypeUnitsName === 'Hours' && x.include && !x.removed
                ? getDuration(timesheet.date, x.start, x.end, x.breakStart, x.breakEnd)
                : 0.0))
            .reduce((a, b) => a + b, 0);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = (totalMinutes % 60).toString().padStart(2, '0');
        return t('timesheet:durationFormat', { hours, minutes });
    };

    setSaving = (b) => this.setState({ saving: b });

    create = async (timesheet) => {
        const { nonFlagging } = { ...this.state };

        const response = await httpClient.andGetResponse(
            httpClient.types.post,
            ApiRoutes.selfDispatchTimesheet.create(),
            { ...timesheet },
            'Error Creating Timesheet',
        );

        if (!response) {
            this.setState({ saving: false });
            return false;
        }

        if (response.result === ServiceResponseResult.Ok) {
            toasty.success(this.props.t('timesheet:saved'));
            const url = nonFlagging
                ? AppNavPaths.NonFlaggingTimesheet
                : AppNavPaths.SelfDispatchTimesheet;
            this.context.history.push(`${url}/${response.data}`);
        } else {
            toast.error(
                <ToastMessage
                    icon={faExclamationTriangle}
                    header={this.props.t('timesheet:errorSavingHeader')}
                    message={`${response.message}`}
                />,
            );
            this.setState({ saving: false });
        }
    };

    // #endregion

    // #region RENDERING


    handleSaveError = (err) => handleFormSaveError(this, err);

    handlers = {
        change: onFieldChange,
        select: onReactSelectChanged,
    };
    // #endregion

    isValueInvalid() {
        return !this.state.selectedDetail?.value;
    }

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();

        if (!isAuthenticated) {
            return;
        }

        try {
            const { id } = this.props.match.params;
            const { user } = this.context;

            // Check tenant settings and user permissions for access to either self-dispatch ts or
            // non-flag ts.
            const selfDispatchTimesheetsEnabled = this.context?.tenant?.tenantSettings.enableSelfDispatchTimesheets;
            if (!selfDispatchTimesheetsEnabled) return <NotAuthorizedPage />;

            const nonFlagging = this.props.location.pathname.indexOf(
                AppNavPaths.NonFlaggingTimesheet,
            ) > -1
                || this.props.location.pathname.indexOf(
                    AppNavPaths.NonFlaggingTimesheetNew,
                ) > -1;

            if (!nonFlagging) {
                if (!user.canCreateSelfDispatchTimesheet) return <NotAuthorizedPage />;
            } else if (!user.canCreateNonFlagTimesheet) return <NotAuthorizedPage />;

            let [nonFlagContract, contracts, timesheet] = await Promise.all([
                nonFlagging
                    ? httpClient.js(ApiRoutes.contract.getNonFlagging())
                    : null,
                !nonFlagging
                    ? httpClient.js(ApiRoutes.contract.getSelfDispatching())
                    : [],
                id
                    ? httpClient.js(ApiRoutes.selfDispatchTimesheet.byId(id))
                    : new SelfDispatchTimesheet(),
            ]);

            if (!timesheet) {
                this.context.history.push(AppNavPaths.NotFound);
                return false;
            }

            // If we don't have the contract set up in the system, notify.
            if (nonFlagging && !nonFlagContract) return <ContractNotSetUpPage nonFlagging={nonFlagging} />;

            // On new nonFlag timesheets, set the contract to the system's nonFlag
            // contract (should only be one per tenant).
            if (!id) {
                timesheet.contractId = nonFlagContract.id;
                timesheet.contractDescription = nonFlagContract.description;
                timesheet.nonFlagging = nonFlagging;
                contracts = [nonFlagContract];
            } else {
                const contract = await httpClient.js(
                    ApiRoutes.contract.byId(timesheet.contractId),
                );
                contracts = [contract];
                // Removed rows by management should not be seen by emps
                timesheet.timeEntries = timesheet.timeEntries.filter(
                    (x) => !x.removed,
                );
            }

            // Get unique groups for user
            const userGroups = [
                ...new Set(
                    user.appGroups.map(
                        (v) => v.groupName,
                    ),
                ),
            ];
            // Get only contract charges that the user is associated with
            contracts.forEach((contract) => {
                const chargetypes = contract.contractChargeTypes;
                contract.contractChargeTypes = chargetypes.filter((ct) => userGroups.includes(ct.group));
            });

            const originalData = JSON.stringify({ ...timesheet });

            // The user may edit the timesheet on new entries or rejection
            const editable = timesheet.statusId < TimesheetStatus.Submitted
                || timesheet.statusId === TimesheetStatus.Rejected;

            timesheet.timeEntries = sortTimesheetEntries([
                ...timesheet.timeEntries,
            ]);

            this.setState({
                editable,
                newContract: !id,
                nonFlagging,
                nonFlagContract,
                contracts,
                originalData,
                loading: false,
                saving: false,
                errors: [],
                timesheet,
            });
        } catch (error) {
            console.error(error);
            this.context.history.push(AppNavPaths.ServerError);
            return false;
        }
    }

    toggleTimeEntryNotePopover = (inx) => {
        const { notesIndexDisplay } = { ...this.state };
        if (notesIndexDisplay === inx) this.setState({ notesIndexDisplay: null });
        else this.setState({ notesIndexDisplay: inx });
    };

    update = async (timesheet) => {
        const ts = { ...timesheet };
        ts.statusId = TimesheetStatus.Submitted;
        ts.status = 'Submitted';

        const response = await httpClient.andGetResponse(
            httpClient.types.put,
            ApiRoutes.selfDispatchTimesheet.byId(timesheet.id),
            ts,
            'Error Saving Timesheet',
        );

        if (!response) {
            this.setState({ saving: false });
            return false;
        }

        if (response.result === ServiceResponseResult.Ok) {
            toasty.success(this.props.t('timesheet:saved'));
            this.context.history.go(0);
        } else {
            toast.error(
                <ToastMessage
                    icon={faExclamationTriangle}
                    header={this.props.t('timesheet:errorSavingUnexpectedHeader')}
                    message={`${response.message}`}
                />,
            );
            this.setState({ saving: false });
        }
    };

    delete = async ({ timesheetDeletionNotes }) => {
        const ts = { ...this.state };

        ts.statusId = TimesheetStatus.Deleted;
        ts.status = 'Deleted';


        const data = {
            timesheetId: ts.timesheet?.id,
            note: timesheetDeletionNotes ?? '',
        };

        this.setState({ saving: true });

        try {
            await httpClient.andGetResponse(
                httpClient.types.post,
                ApiRoutes.selfDispatchTimesheet.softDelete(),
                data,
                'Error Soft Deleting Timesheet',
            );

            toasty.success(this.props.t('timesheet:deleted'));
            this.context.history.push(`${AppNavPaths.EmployeeTimesheets}`);
        } catch (ex) {
            toast.error(
                <ToastMessage
                    icon={faExclamationTriangle}
                    header={this.props.t('timesheet:errorDeletingUnexpectedHeader')}
                    message={`${ex}`}
                />,
            );
        }
    };

    showDeleteModal = () => {
        ConfirmDelete.show({
            onSubmit: (values) => this.delete(values),
            title: this.props.t('timesheet:deleteTimesheet'),
            body: () => (
                <>
                    <p>{this.props.t('timesheet:confirmDelete')}</p>

                    <Textarea
                        label={this.props.t('timesheet:notes')}
                        id="timesheetDeletionNotes"
                        name="timesheetDeletionNotes"
                        className="form-control"
                        placeholder={this.props.t('timesheet:deletionNotesPlaceholder')}
                        maxLength="500"
                        rows="5"
                        required
                    />
                </>
            ),
        });
    };

    renderForm() {
        const {
            editable,
            editingTimeIndex,
            contracts,
            originalData,
            saving,
            showAddChargeModal,
            timesheet,
            nonFlagging,
            selectedDetail,
            addTimeFormValidated,
            formValidated,
            clientSigIsComplete,
            equipmentOptions,
            equipmentLoading,
        } = { ...this.state };

        const newTimesheet = parseInt(timesheet?.id ?? 0) <= 0;

        const { tenantSettings } = this.context.tenant;

        if (!timesheet || !tenantSettings) return '';

        const jTimesheet = JSON.stringify({ ...timesheet });

        const time = timesheet?.timeEntries ?? [];

        // Check the date selection before allowing time entry.
        let hasValidDate = false;
        if (timesheet?.date) hasValidDate = moment(timesheet?.date).isValid();

        const selectedContract = (contracts ?? []).find(
            (x) => x.id === timesheet?.contractId,
        );
        const selectedContractChargeType = (selectedContract?.contractChargeTypes ?? []).find(
            (x) => x.chargeTypeId === selectedDetail?.chargeTypeId,
        ) ?? '';

        const selectedIncrement = selectedContractChargeType?.configurations
            ? selectedContractChargeType.configurations[0].increment
            : '';

        // const notesLength = (selectedDetail?.notes ?? '').length;
        const { t } = this.props;
        let formHeading = t('timesheet:employeeTimesheet');

        if (newTimesheet) {
            formHeading = t('timesheet:addTimesheet');
        } else if (editable) {
            formHeading = t('timesheet:resubmitTimesheet');
        }

        return (
            <>
                {!!editable && (
                    <FormPrompt
                        when={!saving && !isEqual(originalData, jTimesheet)}
                        message={t('timesheet:unsavedChangesPrompt')}
                    />
                )}
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="selfDispatchTimesheetForm"
                    formHeadingIcon={faUserClock}
                    formHeading={formHeading}
                    formName="selfDispatchTimesheetForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={this.state.saving}
                    errors={this.state.errors}
                    loading={this.state.loading}
                    viewOnly={!editable}
                    backUrl={AppNavPaths.EmployeeTimesheets}
                    backLabel={t('timesheet:backToMyTimesheets')}
                >

                    <FormBlocker
                        showProgress
                        show={!!saving}
                    />
                    <Row
                        className={`self-dispatch-timesheet-content${editable ? ' editable' : ''
                            }`}
                    >
                        <Col
                            xl="6"
                            lg="8"
                            md="10"
                            sm="12"
                            className="ml-auto mr-auto pt-2"
                        >
                            {(timesheet?.statusId ?? 0)
                                >= TimesheetStatus.Submitted && (
                                    <>
                                        <FormGroup>
                                            <FormLabel
                                                label={t('timesheet:status')}
                                                required={false}
                                            />
                                            {this.getTimesheetStatusAlert()}
                                        </FormGroup>

                                        <FormGroup>
                                            <FormLabel
                                                label={t('timesheet:employee')}
                                                required={false}
                                            />
                                            <span className="form-control form-control-plaintext">
                                                {timesheet.employeeName}
                                            </span>
                                        </FormGroup>
                                    </>
                                )}

                            <Input
                                label={t('timesheet:date')}
                                required
                                id="timesheetDate"
                                name="timesheet.date"
                                className={`form-control rounded-left${editable ? ' rounded-right' : ''
                                    }`}
                                value={
                                    moment(timesheet?.date).format(
                                        MomentDateFormat.DateOnlyUTCHyphenated,
                                    ) ?? ''
                                }
                                onChange={(ev) => {
                                    const val = ev?.target?.value;
                                    this.setState(
                                        (state) => (
                                            (state.timesheet.date = moment(
                                                val,
                                            ).format(
                                                MomentDateFormat.DateOnlyUTCHyphenated,
                                            )),
                                            state
                                        ),
                                    );
                                }}
                                type="date"
                                error={t('timesheet:dateRequired')}
                            />


                            {!nonFlagging && (
                                <ValidatedSelect
                                    name="contractDropdown"
                                    label={t('timesheet:contract')}
                                    required
                                    options={contracts}
                                    value={selectedContract}
                                    onChange={
                                        this.onSelectedContractChanged
                                    }
                                    error="Increment is required."
                                />
                            )}

                            <FormGroup>
                                {!!editable && (
                                    <FlexBetweenRow className="pb-2">
                                        <FormLabel
                                            label={t('timesheet:time')}
                                            className="p-0 m-0"
                                            required={!!editable}
                                        />
                                        {!!timesheet?.contractId
                                            && !!hasValidDate && (
                                                <Button
                                                    color="link"
                                                    className="p-0"
                                                    onClick={
                                                        this.onAddTimeClicked
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faPlus}
                                                        className="mr-2"
                                                    />
                                                    <small>{t('timesheet:addCharge')}</small>
                                                </Button>
                                            )}
                                    </FlexBetweenRow>
                                )}
                                {
                                    <>
                                        <ListGroup>
                                            {!time.length && !!hasValidDate && (
                                                <ListGroupItem className="text-center">
                                                    {t('timesheet:noEntriesAdded')}
                                                </ListGroupItem>
                                            )}
                                            {!time.length && !hasValidDate && (
                                                <ListGroupItem className="text-center">
                                                    {t('timesheet:pleaseSelectDate')}
                                                </ListGroupItem>
                                            )}
                                            {!!time.length
                                                && time.map((tsdVm, inx) => {
                                                    const entryDuration = `${moment(
                                                        tsdVm.start,
                                                        MomentTimeFormat.TwentyFourHourWithLeading,
                                                    ).format(
                                                        MomentTimeFormat.AnteTwelveHour,
                                                    )} - ${moment(
                                                        tsdVm.end,
                                                        MomentTimeFormat.TwentyFourHourWithLeading,
                                                    ).format(
                                                        MomentTimeFormat.AnteTwelveHour,
                                                    )}`;

                                                    return (
                                                        <ListGroupItem
                                                            key={inx}
                                                            className={`d-flex flex-row align-items-center p-0 time-entry-item${tsdVm.isAdjustment
                                                                ? ' is-adjustment'
                                                                : ''
                                                                }`}
                                                        >
                                                            <Button
                                                                color="link"
                                                                onClick={() => this.onEditTimeClicked(
                                                                    tsdVm,
                                                                    inx,
                                                                )}
                                                                title={t('timesheet:editTimeEntryTooltip')}
                                                                className={`d-flex flex-column flex-fill p-2${editable
                                                                    && !tsdVm.isAdjustment
                                                                    ? ' cursor-pointer'
                                                                    : ''
                                                                    }`}
                                                            >
                                                                <FlexBetweenRow>
                                                                    <span className="font-weight-bold">
                                                                        {!!tsdVm.isAdjustment && (
                                                                            <Badge
                                                                                color="warning"
                                                                                className="p-1 mr-2"
                                                                                style={{
                                                                                    lineHeight:
                                                                                        '1rem',
                                                                                }}
                                                                            >
                                                                                {t('timesheet:adjBadge')}
                                                                            </Badge>
                                                                        )}
                                                                        {
                                                                            tsdVm.chargeTypeName
                                                                        }
                                                                    </span>
                                                                    <FlexEndRow>
                                                                        {/* <span */}
                                                                        {/*    hidden={!tsdVm.notes} */}
                                                                        {/*    title={tsdVm.notes} */}
                                                                        {/*    className="fa fa-sticky-note mr-2" */}
                                                                        {/* > */}
                                                                        {/* </span> */}
                                                                        <span className="font-weight-bold">
                                                                            {tsdVm.chargeTypeUnitsId
                                                                                === ChargeTypeUnits.Hours
                                                                                && getDuration(
                                                                                    timesheet.date,
                                                                                    tsdVm.start,
                                                                                    tsdVm.end,
                                                                                    tsdVm.breakStart,
                                                                                    tsdVm.breakEnd,
                                                                                    true,
                                                                                )}
                                                                            {!!(
                                                                                tsdVm.chargeTypeUnitsId
                                                                                === ChargeTypeUnits.Flat
                                                                            )
                                                                                && `${parseFloat(
                                                                                    tsdVm.value,
                                                                                ).toFixed(
                                                                                    2,
                                                                                )} (Flat)`}
                                                                        </span>
                                                                    </FlexEndRow>
                                                                </FlexBetweenRow>
                                                                <FlexEndRow>
                                                                    <span className="text-muted d-flex flex-row align-items-center flex-nowrap">
                                                                        {!!(
                                                                            tsdVm.chargeTypeUnitsId
                                                                            === ChargeTypeUnits.Hours
                                                                        )
                                                                            && entryDuration}
                                                                        {!!(
                                                                            tsdVm.chargeTypeUnitsId
                                                                            === ChargeTypeUnits.Flat
                                                                        )
                                                                            && !!tsdVm.equipmentId && (
                                                                                <>
                                                                                    <span>
                                                                                        {tsdVm.equipmentType}
                                                                                        {' - '}
                                                                                    </span>
                                                                                    <strong>
                                                                                        {
                                                                                            tsdVm.equipmentType
                                                                                        }
                                                                                    </strong>
                                                                                </>
                                                                            )}
                                                                    </span>
                                                                </FlexEndRow>

                                                                <Answers
                                                                    answers={tsdVm.answers}
                                                                />

                                                                {!!tsdVm.notes && (
                                                                    <FlexColumnCenter className="mt-2 ml-3 mb-2">
                                                                        <FlexStartRow className="border-bottom">
                                                                            <small className="font-weight-bold text-info">
                                                                                {t('timesheet:notesUpper')}
                                                                            </small>
                                                                        </FlexStartRow>
                                                                        <FlexStartRow className="pt-1">
                                                                            <small>
                                                                                {
                                                                                    tsdVm.notes
                                                                                }
                                                                            </small>
                                                                        </FlexStartRow>
                                                                    </FlexColumnCenter>
                                                                )}
                                                            </Button>
                                                        </ListGroupItem>
                                                    );
                                                })}
                                        </ListGroup>
                                        <Input
                                            required={newTimesheet}
                                            id="timeEntriesAdded"
                                            className="hidden-input-validation"
                                            value={
                                                (
                                                    this.state.timesheet
                                                        ?.timeEntries ?? []
                                                ).length
                                                    ? 'yes'
                                                    : ''
                                            }
                                            error="At least one time entry is required"
                                        />
                                    </>
                                }
                            </FormGroup>
                            <hr />
                            <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                                <FormLabel
                                    className="mb-0 w-100"
                                    htmlFor="totalDurationAll"
                                    label={t('timesheet:total')}
                                    required={false}
                                />
                                <div className="d-flex flex-row flex-nowrap align-items-center justify-content-start">
                                    <span
                                        id="totalDurationAll"
                                        className="h3 font-weight-bold text-muted m-0"
                                    >
                                        {!!timesheet?.timeEntries
                                            && this.getTotalDuration()}
                                    </span>
                                </div>
                            </FormGroup>
                            <hr />
                            {!!timesheet?.employeeReview?.isComplete && (
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="empSigPreview"
                                        label={`${t('timesheet:employeeSignature')}:`}
                                    />
                                    <SignatureImage
                                        alt={t('timesheet:employeeSignaturePreview')}
                                        id="empSigPreview"
                                        style={{
                                            maxWidth: '100%',
                                            height: 'auto',
                                            border: '1px solid #ccc',
                                        }}
                                        src={
                                            timesheet?.employeeReview
                                                ?.signatureData
                                        }
                                    />
                                </FormGroup>
                            )}
                            {!timesheet?.employeeReview?.isComplete && (
                                <SignatureButton
                                    id="employeeSignatureButton"
                                    onSigSavedCallback={
                                        this.onEmployeeSigSavedCallback
                                    }
                                    sigData={null}
                                    reviewId={timesheet?.employeeReview?.id}
                                    signatureLabel={t('timesheet:employee')}
                                />
                            )}
                            {
                                // Contract requires client sig
                                (!!selectedContract?.requireSelfDispatchTimesheetsClientSignature
                                    // If at some point we captured the sig - even if the contract was since modified to not require
                                    // the client sig - we should to historically display it regardless.
                                    || !!timesheet?.clientReview
                                        ?.signatureData)
                                && (
                                    <>
                                        <hr />
                                        <ClientTimesheetSignature
                                            review={timesheet?.clientReview}
                                            onSignatureTypeChanged={
                                                this.onSignatureTypeChanged
                                            }
                                            onMissingSigReasonChanged={
                                                this.onMissingSigReasonChanged
                                            }
                                            onChangeNotes={this.onChangeNotes}
                                            onSigSavedCallback={
                                                this.onClientSigSavedCallback
                                            }
                                            signatureRef={
                                                this.clientSignatureRef
                                            }
                                            tenantSettings={tenantSettings}
                                            formValidated={formValidated}
                                            sigIsComplete={clientSigIsComplete}
                                        />
                                    </>
                                )
                            }
                            {!!timesheet.timesheetNumber && (
                                <FormGroup>
                                    <FormLabel
                                        label={t('timesheet:timesheetNumber')}
                                        required={false}
                                    />
                                    <span className="form-control form-control-plaintext">
                                        {timesheet.timesheetNumber}
                                    </span>
                                </FormGroup>
                            )}
                            {!!editable && (
                                <>
                                    <hr />
                                    <FlexCenterRow className="mt-3 mb-3 w-100">
                                        <Button
                                            disabled={
                                                !timesheet?.employeeReview
                                                    ?.signatureData
                                            }
                                            type="submit"
                                            color="success"
                                            name="selfDispatchTimesheetForm"
                                            className="w-100"
                                        >
                                            <FontAwesomeIcon
                                                className="mr-2"
                                                icon={faSave}
                                            />
                                            {newTimesheet
                                                ? t('timesheet:submitTimesheet')
                                                : t('timesheet:resubmitTimesheet')}
                                        </Button>
                                    </FlexCenterRow>
                                </>
                            )}
                            {TimesheetStatus.Rejected && !!editable && !newTimesheet && (
                                <FlexCenterRow className="mt-3 mb-3 w-100">
                                    <Button
                                        type="button"
                                        color="outline-danger"
                                        className="w-100"
                                        onClick={() => this.showDeleteModal()}
                                    >
                                        <FontAwesomeIcon
                                            className="mr-2"
                                            icon={faTrashAlt}
                                        />
                                        {t('timesheet:deleteTimesheet')}
                                    </Button>
                                </FlexCenterRow>
                            )}
                        </Col>
                    </Row>
                </AppPageForm>

                <ModalForm
                    isOpen={showAddChargeModal}
                    onSubmit={() => this.onAddTimeSubmit()}
                    toggle={() => this.setState({
                        currentTimeEntry: null,
                        showAddChargeModal: false,
                    })}
                    title={!Number.isNaN(parseInt(editingTimeIndex))
                        ? t('timesheet:editTime')
                        : t('timesheet:addTime')}
                    SaveButtonProps={{
                        label: t('timesheet:saveTimeEntry'),
                    }}
                    className="full-screen-modal"
                >
                    <ValidatedSelect
                        menuPosition="fixed" /* RLC: When being in an overflow container, this will prevent cutoff. */
                        maxMenuHeight={150}
                        isClearable={false}
                        label={t('timesheet:charge')}
                        name="chargeTypesDropdown"
                        required
                        isDisabled={!!selectedDetail?.id}
                        value={selectedContractChargeType}
                        options={
                            selectedContract?.contractChargeTypes
                            ?? []
                        }
                        onChange={this.onChargeTypeChanged}
                        getOptionLabel={(o) => o.description}
                        getOptionValue={(o) => o.chargeTypeId}
                        error={t('timesheet:chargeRequired')}
                    />

                    {!!selectedContractChargeType?.equipment && (
                        <FormGroup>
                            {equipmentLoading ? (
                                <FlexStartRow>
                                    <Spinner
                                        className="circular-progress-equipment-loading"
                                    />
                                    <span className="ml-2">
                                        {t('timesheet:loadingEquipment')}
                                    </span>
                                </FlexStartRow>
                            ) : (
                                <ValidatedSelect
                                    menuPosition="fixed" /* RLC: When being in an overflow container, this will prevent cutoff. */
                                    isClearable={false}
                                    name="equipmentDropdown"
                                    label={t('timesheet:equipment')}
                                    required={
                                        !!selectedContractChargeType?.equipment
                                        && !!editable
                                    }
                                    isDisabled={!editable}
                                    value={
                                        (
                                            equipmentOptions ?? []
                                        ).find(
                                            (eo) => eo.id
                                                === selectedDetail?.equipmentId,
                                        ) ?? ''
                                    }
                                    options={equipmentOptions ?? []}
                                    onChange={
                                        this.onEquipmentChanged
                                    }
                                    getOptionLabel={(o) => o.description}
                                    getOptionValue={(o) => o.id}
                                    error={t('timesheet:equipmentRequired')}
                                />
                            )}
                        </FormGroup>
                    )}
                    {selectedContractChargeType?.chargeUnits
                        === 'Flat' && (
                            <NumericInput
                                label={t('timesheet:units')}
                                disabled={!editable}
                                name="value"
                                required={!!editable}
                                value={selectedDetail.value ?? ''}
                                onChange={(event) => {
                                    const { selectedDetail } = {
                                        ...this.state,
                                    };
                                    const { value } = event.target;
                                    selectedDetail.value = value;
                                    this.setState({
                                        selectedDetail,
                                    });
                                }}
                                placeholder={t('timesheet:enterValue')}
                                step={selectedIncrement ?? 1}
                                error={
                                    !!addTimeFormValidated && !!this.isValueInvalid() && t('timesheet:valueRequired')
                                }
                            />
                        )}
                    {selectedContractChargeType?.chargeUnits
                        === 'Hours' && (
                            <>
                                <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                                    <FormLabel
                                        className="mb-0"
                                        htmlFor="start"
                                        label={t('timesheet:startTime')}
                                        required={!!editable}
                                    />
                                    <FlexColumnStart className="w-100">
                                        <TimePicker
                                            disabled={!editable}
                                            required={!!editable}
                                            name="start"
                                            value={selectedDetail.start}
                                            increment={selectedIncrement}
                                            onChange={this.onChargeChange}
                                        />
                                        <small className="invalid-feedback text-danger">
                                            {t('timesheet:startTimeRequired')}
                                        </small>
                                    </FlexColumnStart>
                                </FormGroup>
                                <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                                    <FormLabel
                                        className="mb-0 flex-fill"
                                        htmlFor="chargeEndTime"
                                        label={t('timesheet:endTime')}
                                        required={!!editable}
                                    />
                                    <FlexColumnStart className="w-100">
                                        <TimePicker
                                            disabled={
                                                !selectedDetail?.start
                                                || !editable
                                            }
                                            required={!!editable}
                                            name="end"
                                            value={selectedDetail.end}
                                            increment={selectedIncrement}
                                            onChange={this.onChargeChange}
                                        />
                                        <small className="invalid-feedback text-danger">
                                            {t('timesheet:endTimeRequired')}
                                        </small>
                                    </FlexColumnStart>
                                </FormGroup>

                                {isBreakAllowed(
                                    selectedContractChargeType,
                                )
                                    && (
                                        <>
                                            <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                                                <FormLabel
                                                    className="mb-0"
                                                    htmlFor="breakStart"
                                                    text={`${getBreakName(
                                                        selectedContractChargeType,
                                                    )} Start`}
                                                />
                                                <FlexColumnStart className="w-100">
                                                    <TimePicker
                                                        disabled={!editable}
                                                        name="breakStart"
                                                        value={
                                                            selectedDetail.breakStart
                                                        }
                                                        increment={
                                                            selectedIncrement
                                                        }
                                                        onChange={
                                                            this.onChargeChange
                                                        }
                                                    />
                                                </FlexColumnStart>
                                            </FormGroup>
                                            <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                                                <FormLabel
                                                    className="mb-0 flex-fill"
                                                    htmlFor="breakEnd"
                                                    text={`${getBreakName(
                                                        selectedContractChargeType,
                                                    )} End`}
                                                />
                                                <FlexColumnStart className="w-100">
                                                    <TimePicker
                                                        disabled={
                                                            !selectedDetail?.breakStart
                                                            || !editable
                                                        }
                                                        name="breakEnd"
                                                        value={
                                                            selectedDetail.breakEnd
                                                        }
                                                        increment={
                                                            selectedIncrement
                                                        }
                                                        onChange={
                                                            this.onChargeChange
                                                        }
                                                    />
                                                </FlexColumnStart>
                                            </FormGroup>
                                        </>
                                    )}

                                <div className="p-0 mb-2 border-top" />
                                <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                                    <FormLabel
                                        className="mb-0 w-100"
                                        htmlFor="totalDuration"
                                        label={t('timesheet:total')}
                                        required={false}
                                    />
                                    <div className="d-flex flex-row flex-nowrap align-items-center justify-content-start">
                                        <span
                                            id="totalDuration"
                                            className="h3 font-weight-bold text-muted m-0"
                                        >
                                            {getDuration(
                                                timesheet?.date,
                                                selectedDetail?.start,
                                                selectedDetail?.end,
                                                selectedDetail?.breakStart,
                                                selectedDetail?.breakEnd,
                                                true,
                                            )}
                                        </span>
                                    </div>
                                </FormGroup>
                                <div className="p-0 mb-2 border-top" />
                            </>
                        )}
                    {!!(selectedContractChargeType?.questions ?? [])
                        .length
                        && selectedContractChargeType.questions
                            .sort((q1, q2) => q1.order - q2.order)
                            .map((q, qInx) => (
                                <SurveyQuestion
                                    disabled={!editable}
                                    key={qInx}
                                    question={q}
                                    questionIndex={qInx}
                                    answers={selectedDetail.answers}
                                    onTextAnswerChanged={(event) => this.onDetailTextAnswerChanged(
                                        event,
                                        q,
                                        'selectedDetail',
                                        'answers',
                                    )}
                                    onSelectAnswerChanged={(
                                        selection,
                                    ) => this.onDetailSelectAnswerChanged(
                                        selection,
                                        q,
                                        'selectedDetail',
                                        'answers',
                                    )}
                                    onSelectChanged={(selection) => this.onDetailSelectChanged(
                                        selection,
                                        q,
                                        'selectedDetail',
                                        'answers',
                                    )}
                                />
                            ))}

                    {!!editable && !Number.isNaN(parseInt(editingTimeIndex)) && (
                        <FlexCenterRow className="w-100 mt-4">
                            <Button
                                title={t('timesheet:removeTimeEntryTooltip')}
                                color="outline-danger"
                                className="cursor-pointer"
                                onClick={() => this.onRemoveTimeEntryClicked(
                                    editingTimeIndex,
                                )}
                            >
                                <FontAwesomeIcon
                                    icon={faTrash}
                                    className="mr-2"
                                />
                                {t('timesheet:deleteTimeEntry')}
                            </Button>
                        </FlexCenterRow>
                    )}
                </ModalForm>
            </>
        );
    }

    render() {
        if (this.state.loading || !this.context.tenant.tenantSettings) {
            return (
                <div className="confirmationContainer pt-2 d-flex flex-row align-items-center justify-content-center">
                    <CustomCircularProgress
                        color="secondary"
                        variant="indeterminate"
                        label={
                            this.props.match.params.id
                                ? 'Getting timesheet...'
                                : 'Loading form...'
                        }
                    />
                </div>
            );
        }
        return this.renderForm();
    }
    // #endregion
}
export default withTranslation()(SelfDispatchTimesheetForm);
